import { useLogger } from "@1js/pl-logger";
import { Skeleton, SkeletonItem, TabList } from "@fluentui/react-components";
import React from "react";
import type { Appearance } from "../data";
import { BookmarkTab } from "./BookmarkTab";
import { GroupTab } from "./GroupTab";
import { PromptTabs } from "./PromptTabs";
import { SuggestedTab } from "./SuggestedTab";
import { useStyles } from "./Tab.styles";
import { WorkplaceTab } from "./WorkplaceTab";

export type PromptTabProps = {
  loading?: boolean;
  isShowGroupTab?: boolean;
  isShowSavedTab?: boolean;
  isShowWorkplaceTab?: boolean;
  isRocksteadyUxEnabled?: boolean;
  appearance: Appearance;
  selectedValue?: PromptTabs;
  onTabSelected?: (selectedTab: PromptTabs) => void;
};

export const PromptTab: React.FC<PromptTabProps> = React.memo(
  ({
    loading,
    selectedValue,
    isShowSavedTab = true,
    isShowGroupTab,
    isRocksteadyUxEnabled,
    isShowWorkplaceTab,
    appearance,
    onTabSelected,
  }) => {
    const logger = useLogger();
    const savedTabRef = React.useRef<HTMLButtonElement>(null);
    const isTeamsTab = localStorage.getItem("IsTeamsTab");
    const classes = useStyles(appearance)();

    // when user gets redirected to the save tab from other tabs, focus on the saved tab for accessibility
    React.useEffect(() => {
      if (selectedValue === PromptTabs.SavedPrompts && savedTabRef?.current) {
        savedTabRef.current.focus();
      }
    }, [selectedValue]);

    const handleOnTabSelect = React.useCallback(
      (value: PromptTabs) => {
        if (value === PromptTabs.SavedPrompts) {
          logger?.promptLibrary.sendResultsRenderedEvent({
            metaData: {
              eventId: "Bookmarked",
            },
          });
        }
        onTabSelected && onTabSelected(value);
      },
      [onTabSelected, logger]
    );

    return loading ? (
      <Skeleton aria-label="Loading Content">
        <SkeletonItem size={32} style={{ width: "160px", marginLeft: "6px" }} />
      </Skeleton>
    ) : (
      <TabList
        className={classes.tabList}
        style={isTeamsTab === "true" ? { display: "none" } : undefined}
        selectedValue={selectedValue}
        onTabSelect={(_, selectedTab) =>
          handleOnTabSelect(selectedTab.value as PromptTabs)
        }
        appearance={"filled-circular"}
      >
        <SuggestedTab
          isSelected={selectedValue === PromptTabs.AllPrompts}
          appearance={appearance}
          isRocksteadyUxEnabled={isRocksteadyUxEnabled}
        />
        {isShowSavedTab && (
          <BookmarkTab
            ref={savedTabRef}
            isSelected={selectedValue === PromptTabs.SavedPrompts}
            appearance={appearance}
          />
        )}
        {isShowWorkplaceTab && (
          <WorkplaceTab
            isSelected={selectedValue === PromptTabs.WorkplacePrompts}
            appearance={appearance}
          />
        )}
        {isShowGroupTab && (
          <GroupTab
            isSelected={selectedValue === PromptTabs.GroupPrompts}
            appearance={appearance}
          />
        )}
      </TabList>
    );
  }
);

PromptTab.displayName = "PromptTab";
